import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Wallet, CheckCircle2, Clock, ExternalLink, IndianRupee,
  RefreshCw, FileText, ChevronRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API } from "@/lib/api";

const STATUS_LABELS = {
  invoice_generated: "Invoice Generated",
  payout_pending: "Payout Pending",
  delivery_assigned: "Delivery Assigned",
  delivered: "Delivered",
  closed: "Closed",
};

const JOURNEY_LABELS = {
  home_to_shop: "Home → Shop → Home",
  shop_to_home: "Shop → Home",
  c2c: "C2C",
};

export const PayeeQueue = () => {
  const [tab, setTab] = useState("pending");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [refs, setRefs] = useState({});

  const fetchOrders = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true); else setRefreshing(true);
    try {
      const res = await fetch(`${API}/payee/queue?status=${tab}`);
      if (!res.ok) throw new Error();
      setOrders(await res.json());
    } catch { toast.error("Failed to load queue"); }
    finally { setLoading(false); setRefreshing(false); }
  }, [tab]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const markPaid = async (order) => {
    const ref = refs[order.id] || "";
    setPayingId(order.id);
    try {
      const res = await fetch(`${API}/orders/${order.id}/payout`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reference: ref, payee_name: "Payee" }),
      });
      if (!res.ok) { let msg = "Failed to mark payout"; try { const d = await res.json(); msg = d.detail || msg; } catch {} throw new Error(msg); }
      toast.success("Payout marked complete!");
      setRefs(p => { const n = { ...p }; delete n[order.id]; return n; });
      fetchOrders(true);
    } catch (e) { toast.error(e.message || "Failed to mark payout"); }
    finally { setPayingId(null); }
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const pendingCount = tab === "pending" ? orders.length : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Payee Queue</h1>
          <p className="text-slate-500 text-sm mt-0.5">Vendor payout management</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => fetchOrders(true)} disabled={refreshing}>
          <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {[
          { key: "pending", label: "Pending Payout", icon: Clock },
          { key: "completed", label: "Completed", icon: CheckCircle2 },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              tab === key ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
            {key === "pending" && pendingCount != null && pendingCount > 0 && (
              <span className="bg-pink-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-pulse text-slate-400">Loading...</div>
        </div>
      ) : orders.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            {tab === "pending" ? (
              <>
                <CheckCircle2 className="w-14 h-14 text-emerald-200 mx-auto mb-4" />
                <h3 className="text-base font-medium text-slate-600 mb-1">All clear!</h3>
                <p className="text-slate-400 text-sm">No pending payouts.</p>
              </>
            ) : (
              <>
                <Wallet className="w-14 h-14 text-slate-200 mx-auto mb-4" />
                <h3 className="text-base font-medium text-slate-600 mb-1">No completed payouts yet</h3>
                <p className="text-slate-400 text-sm">Completed payouts will appear here.</p>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => (
            <Card key={order.id} className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  {/* Left: order info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link
                        to={`/orders/${order.id}`}
                        className="font-bold text-slate-900 hover:text-[#0047FF] transition-colors"
                      >
                        {order.order_number}
                      </Link>
                      <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                        {JOURNEY_LABELS[order.journey_type] || order.journey_type}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">
                        {STATUS_LABELS[order.status] || order.status}
                      </span>
                    </div>

                    <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <div className="text-slate-500">Customer</div>
                      <div className="text-slate-700 font-medium truncate">{order.customer_name}</div>
                      <div className="text-slate-500">Service</div>
                      <div className="text-slate-700">{order.service_type}</div>
                      {order.merchant_name && (
                        <>
                          <div className="text-slate-500">Merchant</div>
                          <div className="text-slate-700 truncate">{order.merchant_name}</div>
                        </>
                      )}
                      {order.invoice_amount > 0 && (
                        <>
                          <div className="text-slate-500">Invoice Amount</div>
                          <div className="font-bold text-slate-900 flex items-center gap-0.5">
                            <IndianRupee className="w-3.5 h-3.5" />
                            {order.invoice_amount.toFixed(2)}
                          </div>
                        </>
                      )}
                      {tab === "completed" && order.payout_reference && (
                        <>
                          <div className="text-slate-500">Ref</div>
                          <div className="text-slate-700 font-mono text-xs">{order.payout_reference}</div>
                        </>
                      )}
                      {tab === "completed" && order.payout_completed_at && (
                        <>
                          <div className="text-slate-500">Paid on</div>
                          <div className="text-slate-700">{fmt(order.payout_completed_at)}</div>
                        </>
                      )}
                    </div>

                    {/* Vendor invoice link */}
                    {order.vendor_invoice_id && (
                      <Link
                        to={`/vendor-invoice/${order.vendor_invoice_id}`}
                        className="inline-flex items-center gap-1.5 mt-2 text-xs text-blue-600 hover:text-blue-800"
                      >
                        <FileText className="w-3.5 h-3.5" /> View Vendor Invoice
                      </Link>
                    )}
                  </div>

                  {/* Right: action */}
                  <div className="sm:w-64 shrink-0">
                    {tab === "pending" ? (
                      <div className="space-y-2">
                        <Label className="text-xs">Payment Reference</Label>
                        <Input
                          value={refs[order.id] || ""}
                          onChange={(e) => setRefs(p => ({ ...p, [order.id]: e.target.value }))}
                          placeholder="UPI / Bank ref (optional)"
                          className="h-9 text-sm"
                        />
                        <Button
                          size="sm"
                          onClick={() => markPaid(order)}
                          disabled={payingId === order.id}
                          className="w-full bg-pink-600 hover:bg-pink-700"
                        >
                          <Wallet className="w-3.5 h-3.5 mr-1.5" />
                          {payingId === order.id ? "Marking..." : "Mark Paid"}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end">
                        <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700 font-medium">
                          <CheckCircle2 className="w-4 h-4" /> Paid
                        </span>
                        <Link to={`/orders/${order.id}`} className="ml-3">
                          <Button size="sm" variant="outline">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
